import React, { useState } from 'react';
import { Pizza, Sandwich, Utensils, UtensilsCrossed, ShoppingBag, Cookie, Download, Info } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { menuData, menuCategories } from '../data/menu';
import MenuItemModal from './MenuItemModal';

const iconMap = {
  Pizza: Pizza,
  Sandwich: Sandwich,
  Utensils: Utensils,
  UtensilsCrossed: UtensilsCrossed,
  ShoppingBag: ShoppingBag,
  Cookie: Cookie
};

// Composant pour afficher les prix
const PriceBadge = ({ label, price, variant = 'default' }) => (
  <Badge 
    className={`text-sm font-bold px-3 py-1 ${
      variant === 'senior' 
        ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' 
        : 'bg-amber-600 text-white hover:bg-amber-600'
    }`}
  >
    {label} {price.toFixed(2)}€
  </Badge>
);

// Composant item cliquable
const ClickableItem = ({ item, onClick, className = '' }) => (
  <button
    onClick={() => onClick(item)}
    className={`bg-amber-50/50 rounded-lg px-3 py-2 text-sm text-gray-700 font-medium text-center hover:bg-amber-200 hover:text-amber-900 transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 group ${className}`}
  >
    <span>{typeof item === 'string' ? item : item.name}</span>
    <Info className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-amber-600" />
  </button>
);

// Section Pizzas (Tomate ou Crème)
const PizzaSection = ({ data, onItemClick }) => (
  <div className="space-y-6">
    {data.priceTiers.map((tier, tierIndex) => (
      <div key={tierIndex} className="bg-white rounded-xl p-4 border border-amber-100">
        <div className="flex flex-wrap gap-2 mb-4">
          <PriceBadge label="Senior" price={tier.prices.senior} variant="senior" />
          <PriceBadge label="Mega" price={tier.prices.mega} variant="mega" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {tier.items.map((item, idx) => (
            <ClickableItem 
              key={idx} 
              item={item}
              onClick={() => onItemClick(item, tier.prices)}
            />
          ))}
        </div>
      </div>
    ))}
    
    {/* Suppléments */}
    {data.supplements && (
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <p className="text-sm font-semibold text-gray-700 mb-3">Suppléments :</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.supplements.map((sup, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <span className="font-medium text-amber-700">{sup.label} :</span>
              <span>Viande +{sup.viande.toFixed(2)}€</span>
              <span className="text-gray-400">|</span>
              <span>Légumes +{sup.legumes.toFixed(2)}€</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Section Offre à Emporter
const OffreSection = ({ data }) => (
  <div className="space-y-4">
    {data.offers.map((offer, idx) => (
      <div key={idx} className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h4 className="text-2xl font-bold">{offer.name}</h4>
            <p className="text-amber-100">{offer.note}</p>
          </div>
          <div className="text-4xl font-black">
            {offer.price.toFixed(2)}€
          </div>
        </div>
      </div>
    ))}
  </div>
);

// Section Calzones
const CalzoneSection = ({ data, onItemClick }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 mb-4">
      <span className="text-lg font-semibold text-gray-700">Prix unique :</span>
      <PriceBadge label="" price={data.price} variant="mega" />
    </div>
    
    {data.categories.map((cat, catIdx) => (
      <div key={catIdx} className="bg-white rounded-xl p-4 border border-amber-100">
        <h4 className="font-semibold text-amber-700 mb-3">Base {cat.base}</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {cat.items.map((item, idx) => (
            <ClickableItem 
              key={idx} 
              item={item}
              onClick={() => onItemClick(item, { unique: data.price })}
              className="justify-start text-left"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Section Naanwich
const NaanwichSection = ({ data, onItemClick }) => (
  <div className="space-y-4">
    <p className="text-gray-600 italic">{data.description}</p>
    
    <div className="flex flex-wrap gap-2 mb-4">
      <PriceBadge label="Seul" price={data.prices.seul} variant="senior" />
      <PriceBadge label="Menu" price={data.prices.menu} variant="mega" />
    </div>
    
    <div className="bg-white rounded-xl p-4 border border-amber-100">
      <p className="text-sm font-semibold text-gray-700 mb-3">Choix (cliquez pour voir la composition) :</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {data.items.map((item, idx) => (
          <ClickableItem 
            key={idx} 
            item={item}
            onClick={() => onItemClick(item, data.prices)}
          />
        ))}
      </div>
    </div>
    
    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
      <span className="font-medium text-amber-700">{data.supplement.label} :</span> +{data.supplement.price.toFixed(2)}€
    </div>
  </div>
);

// Section Snacks
const SnacksSection = ({ data, onItemClick }) => (
  <div className="space-y-4">
    {data.categories.map((cat, catIdx) => (
      <div key={catIdx} className="bg-white rounded-xl p-4 border border-amber-100">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div>
            <button
              onClick={() => cat.composition && onItemClick({ name: cat.name, composition: cat.composition }, cat.prices)}
              className="text-lg font-bold text-gray-800 hover:text-amber-700 transition-colors flex items-center gap-1"
            >
              {cat.name}
              {cat.composition && <Info className="w-4 h-4 text-amber-600" />}
            </button>
            <p className="text-sm text-gray-500 italic">{cat.description}</p>
          </div>
          <div className="flex gap-2">
            <PriceBadge label="Seul" price={cat.prices.seul} variant="senior" />
            <PriceBadge label="Menu" price={cat.prices.menu} variant="mega" />
          </div>
        </div>
        
        {cat.items && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-3">
            {cat.items.map((item, idx) => (
              <ClickableItem 
                key={idx} 
                item={item}
                onClick={() => onItemClick(item, cat.prices)}
              />
            ))}
          </div>
        )}
        
        {cat.supplement && (
          <p className="text-sm text-gray-600 mt-3">
            <span className="font-medium text-amber-700">{cat.supplement.label} :</span> +{cat.supplement.price.toFixed(2)}€
          </p>
        )}
        
        {cat.supplements && (
          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-3">
            {cat.supplements.map((sup, idx) => (
              <span key={idx}>
                <span className="font-medium text-amber-700">{sup.label} :</span> +{sup.price.toFixed(2)}€
              </span>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

// Section Crêpes
const CrepesSection = ({ data, onItemClick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
    {data.items.map((item, idx) => (
      <button 
        key={idx} 
        onClick={() => onItemClick(item, { unique: item.price })}
        className="bg-white rounded-xl p-4 border border-amber-100 flex justify-between items-center hover:border-amber-300 hover:bg-amber-50 transition-all cursor-pointer group"
      >
        <span className="font-medium text-gray-800 flex items-center gap-1">
          {item.name}
          <Info className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-amber-600" />
        </span>
        <Badge className="bg-amber-600 text-white hover:bg-amber-600 font-bold">
          {item.price.toFixed(2)}€
        </Badge>
      </button>
    ))}
  </div>
);

// Composant principal
const MenuSection = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPrices, setSelectedPrices] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleItemClick = (item, prices) => {
    setSelectedItem(item);
    setSelectedPrices(prices);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
    setSelectedPrices(null);
  };

  const renderCategoryContent = (key) => {
    const data = menuData[key];
    
    switch (key) {
      case 'pizzasSauceTomate':
      case 'pizzasCremeFraiche':
        return <PizzaSection data={data} onItemClick={handleItemClick} />;
      case 'offreEmporter':
        return <OffreSection data={data} />;
      case 'calzones':
        return <CalzoneSection data={data} onItemClick={handleItemClick} />;
      case 'naanwich':
        return <NaanwichSection data={data} onItemClick={handleItemClick} />;
      case 'snacks':
        return <SnacksSection data={data} onItemClick={handleItemClick} />;
      case 'crepes':
        return <CrepesSection data={data} onItemClick={handleItemClick} />;
      default:
        return null;
    }
  };

  return (
    <section id="menu" className="py-16 lg:py-24 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Notre Carte
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Menu
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez notre sélection de pizzas artisanales, calzones, naanwich,
            snacks et crêpes. <span className="text-amber-600 font-medium">Cliquez sur un produit pour voir sa composition.</span>
          </p>
        </div>

        {/* Menu Accordion */}
        <Accordion type="multiple" defaultValue={['pizzas-tomate']} className="space-y-4">
          {menuCategories.map((key) => {
            const category = menuData[key];
            const Icon = iconMap[category.icon] || Pizza;
            
            return (
              <AccordionItem 
                key={category.id} 
                value={category.id}
                className="bg-white rounded-2xl border border-amber-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-amber-50/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-amber-600" />
                    </div>
                    <span className="text-xl font-bold text-gray-900 text-left">
                      {category.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  {renderCategoryContent(key)}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Télécharger le menu */}
        <div className="mt-10 text-center">
          <a href="/media/menu.pdf" target="_blank" rel="noopener noreferrer">
            <Button 
              variant="outline" 
              className="border-amber-600 text-amber-700 hover:bg-amber-50 px-6 py-5 text-base font-semibold"
            >
              <Download className="w-5 h-5 mr-2" />
              Télécharger le menu (PDF)
            </Button>
          </a>
          <p className="text-xs text-gray-400 mt-2">Fichier PDF à ajouter dans /public/media/menu.pdf</p>
        </div>
      </div>

      {/* Modal */}
      <MenuItemModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        item={selectedItem}
        prices={selectedPrices}
      />
    </section>
  );
};

export default MenuSection;
